import { Component, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { TrackingActions } from '@toro/common/shared/state-management/tracking/tracking.action';
import { filter, take, map } from 'rxjs/operators';
import { TraderInfoAction } from '@hub-workspace/apps/logged-home/src/app/state-management/trader/trader.action';
import { TraderInfoState } from '@hub-workspace/apps/logged-home/src/app/state-management/trader/trader.reducer';
import { TraderTrialModalComponent } from './trader-trial-modal/trader-trial-modal.component';
import { TraderTrialInfoAction } from '@hub-workspace/apps/logged-home/src/app/state-management/trader-trial/trader-trial.action';
import { TraderClubActions } from '@hub-workspace/apps/logged-home/src/app/state-management/club-trader/club-trader.actions';
import { ProfileTraderClub } from '@hub-workspace/apps/logged-home/src/app/state-management/club-trader/club-trader.reducer';
import { TraderTrial } from '@hub-workspace/apps/logged-home/src/app/state-management/trader-trial/trader-trial.reducer';

enum TypeProfileEnum {
  traderStarter = 'TRADER_STARTER',
  traderMaster = 'TRADER_MASTER',
  traderVip = 'TRADER_VIP',
  traderDefault = 'DEFAULT',
}

const profileNames = {
  [TypeProfileEnum.traderDefault]: 'Default',
  [TypeProfileEnum.traderStarter]: 'Starter',
  [TypeProfileEnum.traderMaster]: 'Master',
  [TypeProfileEnum.traderVip]: 'VIP',
};

const badgeNames = {
  [TypeProfileEnum.traderDefault]: 'icon_no_club',
  [TypeProfileEnum.traderStarter]: 'icon_club_starter',
  [TypeProfileEnum.traderMaster]: 'icon_club_master',
  [TypeProfileEnum.traderVip]: 'icon_club_vip',
};

@Component({
  selector: 'home-trader-club',
  templateUrl: './home-trader-club.component.html',
  styleUrls: ['./home-trader-club.component.scss'],
})
export class HomeTraderClubComponent implements OnInit {
  public trader!: TraderInfoState;
  public profileTraderClub!: ProfileTraderClub;
  public currentTier!: string;
  public showProgress: boolean = true;
  public iconTier!: string;
  public description!: string;
  public starterProgress: number = 0;
  public masterProgress: number = 0;
  public vipProgress: number = 0;
  public traderTrial!: TraderTrial;
  public loadingTraderTrial: boolean = true;
  public hideTraderTrial: boolean = false;
  public trialDuration: number = 0;

  @ViewChild(TraderTrialModalComponent)
  trialModalComponent!: TraderTrialModalComponent;

  private readonly MODAL_EXPIRED_KEY = 'trialModalExpiredSeen';

  constructor(private store: Store<any>) {
    this.store.dispatch({ type: TraderInfoAction.traderInfo });
    this.store.dispatch({ type: TraderTrialInfoAction.traderTrialInfo });
    this.store.dispatch({ type: TraderClubActions.getProfileTrader });
  }

  ngOnInit() {
    this.getTraderInfo();
    this.getTrialInfo();
    this.getProfileInfo();
  }

  public navigateTo(path: string) {
    this.sendTracking('Element Clicked', {
      'Product Area Origin': 'Home Trader',
      Element: 'Veja os benefícios do Clube',
      'Time Responsável': 'Brooklyn Dumbo',
    });
    window.location.href = path;
  }

  public sendTracking(event: string, breakdowns: any) {
    this.store.dispatch({
      type: TrackingActions.trackEvents,
      payload: { type: event, events: breakdowns },
    });
  }

  public getTraderInfo() {
    this.store
      .select((store) => store.trader)
      .pipe(
        filter((trader) => trader && !trader.pending),
        take(1)
      )
      .subscribe((trader) => {
        this.trader = trader;
        this.getCurrentTier();
        this.fillProgressBar();
        this.description = this.calculateContractsToNextLevel();
        this.sendTracking('Loaded a Element', {
          'Product Area Origin': 'Home Trader',
          Category: profileNames[this.trader.profileInfo.typeProfile],
          'Time Responsável': 'Brooklyn Dumbo',
        });
      });
  }

  public getCurrentTier() {
    this.currentTier = profileNames[this.trader.profileInfo.typeProfile];
    this.iconTier = badgeNames[this.trader.profileInfo.typeProfile];
  }

  public calculateContractsToNextLevel(): string {
    if (this.trader.profileInfo.typeProfile === 'DEFAULT') {
      if (this.trader.contractCurrentMonth == 0) {
        return `Opere <strong>${this.trader.clubTrader.starter.minimum}</strong> minicontratos para fazer parte do Clube Trader`;
      }
      let contractsToNextLevel =
        this.trader.clubTrader.starter.minimum -
        this.trader.contractCurrentMonth;
      if (contractsToNextLevel > 0) {
        return `Faltam <strong>${contractsToNextLevel}</strong> contratos para o nível Starter`;
      }
      contractsToNextLevel =
        this.trader.clubTrader.master.minimum -
        this.trader.contractCurrentMonth;
      if (contractsToNextLevel > 0) {
        return `Faltam <strong>${contractsToNextLevel}</strong> contratos para o nível Master`;
      }
      contractsToNextLevel =
        this.trader.clubTrader.vip.minimum - this.trader.contractCurrentMonth;
      if (contractsToNextLevel > 0) {
        return `Faltam <strong>${contractsToNextLevel}</strong> contratos para o nível VIP`;
      }
      this.showProgress = false;
      return 'Parabéns! Você faz parte da <strong>elite de Trading</strong> da Toro.';
    }
    const { minimum } =
      this.trader.clubTrader[
        this.trader.profileInfo.typeProfile.replace('TRADER_', '').toLowerCase()
      ];
    if (
      this.trader.contractCurrentMonth >= this.trader.clubTrader.vip.minimum
    ) {
      this.showProgress = false;
      return `Parabéns! Você faz parte da <strong>elite de Trading</strong> da Toro.`;
    }
    if (this.trader.contractCurrentMonth < minimum) {
      const contractsToMaintainLevel =
        minimum - this.trader.contractCurrentMonth;
      return `Faltam <strong>${contractsToMaintainLevel}</strong> contratos para se manter no nível ${
        profileNames[this.trader.profileInfo.typeProfile]
      }`;
    }
    if (
      this.trader.contractCurrentMonth >= this.trader.clubTrader.master.minimum
    ) {
      const contractsToNextLevel =
        this.trader.clubTrader.vip.minimum - this.trader.contractCurrentMonth;
      return `Faltam <strong>${contractsToNextLevel}</strong> contratos para o nível VIP`;
    }
    if (
      this.trader.contractCurrentMonth >= this.trader.clubTrader.starter.minimum
    ) {
      const contractsToNextLevel =
        this.trader.clubTrader.master.minimum -
        this.trader.contractCurrentMonth;
      return `Faltam <strong>${contractsToNextLevel}</strong> contratos para o nível Master`;
    }
    return 'Parabéns! Você faz parte da <strong>elite de Trading</strong> da Toro.';
  }

  public fillProgressBar() {
    this.getStarterProgress();
    this.getMasterProgress();
    this.getVipProgress();
  }

  public getStarterProgress() {
    this.starterProgress = this.calculateProportion(
      0,
      this.trader.clubTrader.starter.minimum,
      this.trader.contractCurrentMonth
    );
  }

  public getMasterProgress() {
    this.masterProgress = this.calculateProportion(
      this.trader.clubTrader.starter.minimum,
      this.trader.clubTrader.starter.maximum,
      this.trader.contractCurrentMonth
    );
  }

  public getVipProgress() {
    this.vipProgress = this.calculateProportion(
      this.trader.clubTrader.master.minimum,
      this.trader.clubTrader.master.maximum,
      this.trader.contractCurrentMonth
    );
  }

  public calculateProportion(
    lowestValue: number,
    highestValue: number,
    value: number
  ): number {
    if (value < lowestValue) return 0;
    return ((value - lowestValue) / (highestValue - lowestValue)) * 100;
  }

  public getTrialDuration(end: Date): number {
    const now = new Date();
    if (!end || !end.getTime()) {
      return 0;
    }
    if (now > end) {
      return 0;
    }
    const timeDifferenceMs = end.getTime() - now.getTime();
    const millisecondsInADay = 1000 * 60 * 60 * 24;
    const remainingDays = timeDifferenceMs / millisecondsInADay;
    return Math.ceil(remainingDays);
  }

  public getTrialInfo() {
    this.store
      .select((store) => store.traderTrial)
      .pipe(
        filter((state) => state && state.pending === false),
        take(1)
      )
      .subscribe({
        next: (state) => {
          if (state.error) {
            this.loadingTraderTrial = false;
            this.hideTraderTrial = true;
          } else if (state.values && state.values.length > 0) {
            const sortedSubscriptions = [...state.values].sort(
              (a, b) =>
                new Date(b.purchasedAt).getTime() -
                new Date(a.purchasedAt).getTime()
            );
            this.traderTrial = sortedSubscriptions[0];
            this.trialDuration = this.getTrialDuration(
              new Date(this.traderTrial.expiresAt)
            );
            if (
              this.traderTrial &&
              this.traderTrial.status === 'expired' &&
              this.trialModalComponent &&
              !localStorage.getItem(this.MODAL_EXPIRED_KEY)
            ) {
              this.handleExpiration();
            }
          }
          this.loadingTraderTrial = false;
        },
        error: () => {
          this.loadingTraderTrial = false;
          this.hideTraderTrial = true;
        },
      });
  }

  public getProfileInfo() {
    this.store
      .select(({ profileTraderClub }) => profileTraderClub)
      .pipe(
        filter((profileTraderClub) => !!profileTraderClub),
        take(1)
      )
      .subscribe((profileTraderClub) => {
        this.profileTraderClub = { ...profileTraderClub, rankingPosition: 99 };
      });
  }

  public handleExpiration() {
    this.trialModalComponent.onOpen();
    localStorage.setItem(this.MODAL_EXPIRED_KEY, 'true');
  }

  public onTryTraderVip() {
    this.sendTracking('Element Clicked', {
      'Product Area Origin': 'Home Trader',
      Element: 'Botão Experimente o Trader Vip',
      Group: 'Banner Experimente o Trader Vip',
      Text: 'Precisando de ajuda para começar? Conecte-se a especialistas e opere com estratégias compartilhadas.',
      'Time Responsável': 'Sky',
    });
    this.trialModalComponent.onOpen();
  }

  public onCheckTelegramChannel() {
    this.sendTracking('Element Clicked', {
      'Product Area Origin': 'Home Trader',
      Element: 'Hiperlink Canal do Telegram',
      Group: 'Banner Trial Trader Vip',
      Step: 'Período de Teste',
      Text: `${this.trialDuration} dias para encerrar o seu acesso Trader VIP. Aproveite os benefícios.`,
      'Time Responsável': 'Sky',
    });
    window.open('https://t.me/+0lvP3j0cIehkNjcx', '_blank');
  }

  public onActivateTrial() {
    this.store.dispatch({
      type: TraderTrialInfoAction.activateTrialTrader,
    });
  }
}
